import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import LogoMark from './LogoMark';

const navLinks = [
  { name: 'Ana Sayfa', href: '#home', testid: 'nav-link-anasayfa' },
  { name: 'Daireler', href: '#gallery', testid: 'nav-link-daireler' },
  { name: 'Hizmetler', href: '#extras', testid: 'nav-link-hizmetler' },
  { name: 'Galeri', href: '#gallery', testid: 'nav-link-galeri' },
  { name: 'İletişim', href: '#contact', testid: 'nav-link-iletisim' },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      initial={{ y: -90, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
      data-testid="navbar"
      className={`fixed w-full z-50 transition-[background-color,border-color,padding] duration-500 ${isScrolled ? 'bg-black/90 backdrop-blur-md py-2 border-b border-zinc-800/80' : 'bg-transparent py-4 border-b border-transparent'}`}
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between">
          <a href="#home" data-testid="nav-logo" className="flex items-center gap-3 group">
            <LogoMark size={38} className="transition-transform duration-500 group-hover:-rotate-6" />
            <span className="leading-none">
              <span className="block font-display text-lg md:text-xl font-semibold tracking-wide text-white">İSKENDERUN</span>
              <span className="block text-[10px] md:text-xs tracking-[0.45em] text-yellow-500 font-semibold mt-1">KONAKLAMA</span>
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8">
            <div className="flex space-x-7">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  data-testid={link.testid}
                  className="relative text-zinc-300 hover:text-yellow-500 transition-colors text-sm uppercase tracking-wider font-medium after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-yellow-500 after:transition-[width] after:duration-300 hover:after:w-full"
                >
                  {link.name}
                </a>
              ))}
            </div>
            <motion.a
              href="tel:+905416410171"
              data-testid="nav-call-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black px-5 py-2.5 rounded-full font-semibold shadow-lg shadow-yellow-500/20 transition-colors"
            >
              <Phone size={18} />
              <span>Hemen Ara</span>
            </motion.a>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden text-white"
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menüyü aç/kapat"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-md border-b border-zinc-800 shadow-2xl overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col space-y-1 p-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  data-testid={`mobile-${link.testid}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-zinc-300 hover:text-yellow-500 text-lg font-medium py-3 border-b border-zinc-900 transition-colors"
                >
                  {link.name}
                </a>
              ))}
              <a
                href="tel:+905416410171"
                data-testid="mobile-call-button"
                className="flex justify-center items-center gap-2 bg-yellow-500 text-black px-5 py-3 rounded-full font-semibold mt-4"
              >
                <Phone size={20} />
                <span>Hemen Ara</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
